import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { PopoverFilterList } from "@/components/molecules/PopoverFilterList";
import { type IWidgetFilterItem, useWidgetFiltersController } from "./useWidgetFiltersController";

interface IFilterConfig {
    metric: string;
    metricLabel: string;
    items: string[];
}

/**
 * Pre-selection driven by the widget's configured data-filters on this metric (LAD-453).
 * `matchingValues` is authoritative even when empty: a configured filter matching no values
 * means "show none", not "fall back to all". `signature` identifies the configured filter(s);
 * when it changes the filter was edited, so the selection is re-seeded to the new match.
 */
export interface IFilterSeed {
    matchingValues: string[];
    signature: string;
}

export interface IMultiDimensionFilterProps {
    filters: IFilterConfig[];
    controller: ReturnType<typeof useWidgetFiltersController>;
    allSelectedByDefault?: boolean;
    seeds?: Record<string, IFilterSeed>;
}

interface SingleFilterProps {
    filterConfig: IFilterConfig;
    controller: ReturnType<typeof useWidgetFiltersController>;
    allSelectedByDefault: boolean;
    seed?: IFilterSeed;
}

const SingleFilter: React.FC<SingleFilterProps> = ({ filterConfig, controller, allSelectedByDefault, seed }) => {
    const { metric, metricLabel, items } = filterConfig;
    const { selectedFilters, setSelectedFilters } = controller;
    const lastFilterItemsLength = useRef<number>(0);
    const appliedFilterSignature = useRef<string | null>(null);

    const filterItems = useMemo(
        () => items.map((item) => ({ id: item, text: item, data: { value: item } })),
        [items]
    );

    const hasConfiguredFilter = !!seed;
    // With a configured data-filter on this metric, seed exactly the values it matches — even
    // none (the backend skips that filter server-side and relies on this dropdown to apply it).
    // Otherwise keep the historical default of everything selected.
    const seedItems = useMemo(
        () => (seed ? filterItems.filter((item) => seed.matchingValues.includes(item.text)) : filterItems),
        [filterItems, seed]
    );

    const selectedItems = selectedFilters[metric] || [];
    const allItemsSelected = selectedItems.length === filterItems.length;
    const handleSelectionChange = useCallback(
        (newItems: IWidgetFilterItem<object>[]) => {
            setSelectedFilters(metric, newItems);
        },
        [metric, setSelectedFilters]
    );

    useEffect(() => {
        if ((!selectedFilters[metric]) || (filterItems.length !== lastFilterItemsLength.current)) {
            setSelectedFilters(metric, seedItems, true);
        } else if (appliedFilterSignature.current !== null && appliedFilterSignature.current !== (seed?.signature ?? "")) {
            // The configured data-filter changed -> re-seed to the new match so a stale
            // pre-selection from the previous filter doesn't persist (mirrors DimensionFilter).
            setSelectedFilters(metric, seedItems, true);
        }
        appliedFilterSignature.current = seed?.signature ?? "";
        lastFilterItemsLength.current = filterItems.length;
    }, [metric, filterItems, seedItems, setSelectedFilters, selectedFilters, seed])

    return (
        <PopoverFilterList
            selectAllLabel={allItemsSelected ? 'Unselect All' : 'Select All'}
            key={metric}
            items={filterItems}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            triggerLabel={`Filter by ${metricLabel}`}
            allSelectedByDefault={hasConfiguredFilter ? false : allSelectedByDefault}
        />
    );
};

export const MultiDimensionFilter: React.FC<IMultiDimensionFilterProps> = ({
    filters,
    controller,
    allSelectedByDefault = true,
    seeds
}) => {
    const filteredFilters = useMemo(() => filters.filter((filterConfig) => filterConfig.items.length > 0), [filters]);
    return (
        <>
            {filteredFilters.map((filterConfig) => (
                <SingleFilter
                    key={filterConfig.metric}
                    filterConfig={filterConfig}
                    controller={controller}
                    allSelectedByDefault={allSelectedByDefault}
                    seed={seeds?.[filterConfig.metric]}
                />
            ))}
        </>
    );
};
